import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Loader2, Lock, ArrowLeft, RefreshCw, Ticket, Plus, Trash2, Copy, Check, Percent, Coins,
  Mail, QrCode, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmtHuf = (n) => (Number(n) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('hu-HU'); } catch { return '—'; } };

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.get(`${API_BASE}/api/admin/coupons`, { headers: { 'X-Admin-Key': key.trim() } });
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
    } catch (_) { toast.error('Érvénytelen admin kulcs.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-coupons-login" className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <Ticket className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Kuponok</h1>
        <p className="text-center text-sm text-gray-400 mb-6">Add meg az admin kulcsot.</p>
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
        <Button type="submit" disabled={busy || !key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const AdminCouponsPage = () => {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);
  const [qrCode, setQrCode] = useState(null);              // { code } | null
  const [emailFor, setEmailFor] = useState(null);          // { code, discountLine } | null
  const [emailForm, setEmailForm] = useState({ to_email: '', to_name: '', intro_message: '' });
  const [emailBusy, setEmailBusy] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    code: '', label: '', discount_kind: 'pct', discount_pct: 10, discount_huf: 5000,
    max_uses: 1, valid_until: '',
  });
  const [creating, setCreating] = useState(false);

  const headers = useMemo(() => ({ 'X-Admin-Key': adminKey }), [adminKey]);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/coupons`, { headers });
      setRows(Array.isArray(data?.coupons) ? data.coupons : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error('Nem sikerült betölteni a kuponokat.');
      }
    } finally { setLoading(false); }
  }, [adminKey, headers]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        label: form.label || undefined,
        code:  form.code ? form.code.trim() : undefined,
        max_uses: Math.max(1, Number(form.max_uses) || 1),
        valid_until: form.valid_until || undefined,
      };
      if (form.discount_kind === 'pct') payload.discount_pct = Number(form.discount_pct) || 0;
      else                              payload.discount_huf = Number(form.discount_huf) || 0;
      const { data } = await axios.post(`${API_BASE}/api/admin/coupons`, payload, { headers });
      toast.success(`Kupon létrehozva: ${data.coupon.code}`);
      setForm({ ...form, code: '', label: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Létrehozás sikertelen.');
    } finally { setCreating(false); }
  };

  const toggle = async (code, enabled) => {
    try {
      await axios.patch(`${API_BASE}/api/admin/coupons/${code}`, { enabled }, { headers });
      load();
    } catch (_e) { toast.error('Módosítás sikertelen.'); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Biztosan törlöd a "${code}" kupont? Ez visszavonhatatlan.`)) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/coupons/${code}`, { headers });
      toast.success('Törölve.');
      load();
    } catch (_e) { toast.error('Törlés sikertelen.'); }
  };
  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch (_e) { toast.error('Vágólap nem elérhető.'); }
  };

  const openEmailDialog = (row) => {
    const discountLine = row.discount_pct
      ? `${row.discount_pct}% kedvezmény`
      : `${fmtHuf(row.discount_huf || 0)} Ft kedvezmény`;
    setEmailFor({ code: row.code, discountLine });
    setEmailForm({ to_email: '', to_name: '', intro_message: '' });
  };
  const sendEmail = async () => {
    if (!emailFor || !emailForm.to_email.trim()) return;
    setEmailBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/admin/coupons/${emailFor.code}/send-email`,
        {
          to_email:      emailForm.to_email.trim(),
          to_name:       emailForm.to_name.trim() || undefined,
          intro_message: emailForm.intro_message.trim() || undefined,
        },
        { headers },
      );
      if (data.mocked) {
        toast.success('E-mail „elküldve" (mock módban — a preview környezetben mindig mock)');
      } else {
        toast.success(`E-mail elküldve: ${emailForm.to_email}`);
      }
      setEmailFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Küldés sikertelen.');
    } finally { setEmailBusy(false); }
  };

  const couponUrl = (code) => `https://oroszcoaching.hu/?coupon=${code}`;

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  // Stats
  const totalRedemptions = rows.reduce((s, r) => s + (r.uses_count || 0), 0);
  const totalDiscount    = rows.reduce((s, r) => s + (r.redemptions || []).reduce((a, x) => a + (x.discount_huf || 0), 0), 0);
  const activeCount      = rows.filter(r => r.enabled !== false && (r.uses_left || 0) > 0).length;

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="admin-coupons-page">
      <header className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/admin" className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Vissza az admin dashboardra
          </Link>
          <Button variant="outline" onClick={load} className="bg-transparent border-gray-700 text-gray-200 hover:bg-gray-800">
            <RefreshCw size={16} className="mr-2" /> Újratöltés
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-yellow-600/20 flex items-center justify-center">
            <Ticket className="text-yellow-500" size={20} />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Kuponok</h1>
        </div>

        {/* Stat chips */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-gray-800/60 border border-gray-700 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Aktív kupon</div>
            <div className="text-2xl font-black text-yellow-400" data-testid="stat-active-count">{activeCount}</div>
          </div>
          <div className="rounded-xl bg-gray-800/60 border border-gray-700 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Beváltások</div>
            <div className="text-2xl font-black text-yellow-400" data-testid="stat-redemptions">{totalRedemptions}</div>
          </div>
          <div className="rounded-xl bg-gray-800/60 border border-gray-700 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Összes kedvezmény</div>
            <div className="text-2xl font-black text-yellow-400" data-testid="stat-total-discount">{fmtHuf(totalDiscount)} Ft</div>
          </div>
        </div>

        {/* Create form */}
        <form onSubmit={create} className="rounded-xl bg-gray-800/60 border border-gray-700 p-5 space-y-4" data-testid="admin-coupons-create-form">
          <div className="font-bold text-white flex items-center gap-2">
            <Plus size={16} /> Új kupon létrehozása
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Kód (üresen hagyva véletlen)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="pl. SUMMER25" className="bg-gray-900 border-gray-700 text-white font-mono" data-testid="admin-coupons-code-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Belső megjegyzés</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="pl. Kovács Béla konzultáció 2026-02-19" className="bg-gray-900 border-gray-700 text-white" data-testid="admin-coupons-label-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Kedvezmény típusa</Label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_kind: 'pct' })}
                className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center justify-center gap-2 transition-all ${form.discount_kind === 'pct' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-900 text-gray-300 border border-gray-700 hover:border-yellow-500'}`}
                data-testid="admin-coupons-kind-pct"
              >
                <Percent size={14} /> Százalék
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_kind: 'huf' })}
                className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center justify-center gap-2 transition-all ${form.discount_kind === 'huf' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-900 text-gray-300 border border-gray-700 hover:border-yellow-500'}`}
                data-testid="admin-coupons-kind-huf"
              >
                <Coins size={14} /> Fix Ft
              </button>
            </div>
            {form.discount_kind === 'pct' ? (
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Kedvezmény (%)</Label>
                <Input type="number" min={1} max={100} value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="bg-gray-900 border-gray-700 text-white" data-testid="admin-coupons-pct-input" />
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Kedvezmény (Ft)</Label>
                <Input type="number" min={1} value={form.discount_huf} onChange={(e) => setForm({ ...form, discount_huf: e.target.value })} className="bg-gray-900 border-gray-700 text-white" data-testid="admin-coupons-huf-input" />
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Beváltások maximuma</Label>
              <Input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="bg-gray-900 border-gray-700 text-white" data-testid="admin-coupons-max-uses" />
              <p className="text-[11px] text-gray-500 mt-1">Egyszeri beváltásra: 1. Több emberre kiadva: pl. 10.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-gray-400 mb-1.5 block">Lejárati dátum (opcionális)</Label>
              <Input type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-gray-900 border-gray-700 text-white" data-testid="admin-coupons-valid-until" />
            </div>
          </div>

          <Button type="submit" disabled={creating} className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold" data-testid="admin-coupons-create-btn">
            {creating ? <><Loader2 className="mr-2 animate-spin" size={16} /> Létrehozás…</> : <><Plus size={16} className="mr-2" /> Kupon létrehozása</>}
          </Button>
        </form>

        {/* Table */}
        <div className="rounded-xl bg-gray-800/60 border border-gray-700 p-5">
          <div className="font-bold text-white mb-3">Kuponok listája</div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Még nincs kiadott kupon.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-coupons-table">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-700">
                    <th className="text-left py-2 pr-3">Kód</th>
                    <th className="text-left py-2 pr-3">Kedvezmény</th>
                    <th className="text-left py-2 pr-3">Beváltás</th>
                    <th className="text-left py-2 pr-3">Lejár</th>
                    <th className="text-center py-2 pr-3">Aktív</th>
                    <th className="text-right py-2">Műveletek</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isLoyalty = String(r.code || '').startsWith('LOYAL');
                    const reds = Array.isArray(r.redemptions) ? r.redemptions : [];
                    const sends = Array.isArray(r.sends) ? r.sends : [];
                    const isExpanded = expandedCode === r.code;
                    const hasHistory = reds.length > 0 || sends.length > 0;
                    return (
                      <React.Fragment key={r.code}>
                        <tr className="border-b border-gray-800 last:border-0" data-testid={`admin-coupons-row-${r.code}`}>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-2">
                              <code className="font-mono font-bold text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded">{r.code}</code>
                              <button type="button" onClick={() => copyCode(r.code)} className="text-gray-400 hover:text-white" title="Másolás vágólapra" data-testid={`admin-coupons-copy-${r.code}`}>
                                {copiedCode === r.code ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                              </button>
                              {isLoyalty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 font-bold uppercase">Loyalty</span>}
                            </div>
                            {r.label && <div className="text-[11px] text-gray-500 mt-0.5">{r.label}</div>}
                          </td>
                          <td className="py-3 pr-3">
                            {r.discount_pct ? `${r.discount_pct}%` : (r.discount_huf ? `${fmtHuf(r.discount_huf)} Ft` : '—')}
                          </td>
                          <td className="py-3 pr-3">
                            <span className="font-bold text-white">{r.uses_count || 0}</span>
                            <span className="text-gray-500"> / {r.max_uses || (r.uses_count || 0) + (r.uses_left || 0)}</span>
                            {hasHistory && (
                              <button
                                type="button"
                                onClick={() => setExpandedCode(isExpanded ? null : r.code)}
                                className="ml-2 text-yellow-400 hover:text-yellow-300 inline-flex items-center gap-0.5 text-[11px]"
                                data-testid={`admin-coupons-history-toggle-${r.code}`}
                              >
                                {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                Történet
                              </button>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-xs text-gray-400">{fmtDate(r.valid_until)}</td>
                          <td className="py-3 pr-3 text-center">
                            <Switch checked={r.enabled !== false} onCheckedChange={(v) => toggle(r.code, v)} data-testid={`admin-coupons-toggle-${r.code}`} />
                          </td>
                          <td className="py-3 text-right whitespace-nowrap">
                            <button type="button" onClick={() => setQrCode({ code: r.code })} className="text-gray-400 hover:text-yellow-400 inline-flex items-center gap-1 text-xs px-2" title="QR kód" data-testid={`admin-coupons-qr-${r.code}`}>
                              <QrCode size={14} />
                            </button>
                            <button type="button" onClick={() => openEmailDialog(r)} className="text-gray-400 hover:text-yellow-400 inline-flex items-center gap-1 text-xs px-2" title="Küldés emailben" data-testid={`admin-coupons-email-${r.code}`}>
                              <Mail size={14} />
                            </button>
                            <button type="button" onClick={() => remove(r.code)} className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-xs px-2" title="Törlés" data-testid={`admin-coupons-delete-${r.code}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-900/70" data-testid={`admin-coupons-history-${r.code}`}>
                            <td colSpan={6} className="py-4 px-6">
                              <div className="space-y-4">
                                {reds.length > 0 && (
                                  <div>
                                    <div className="text-[11px] uppercase tracking-wide text-emerald-400 font-bold mb-2">Beváltások ({reds.length})</div>
                                    <div className="space-y-1.5">
                                      {reds.map((red, i) => (
                                        <div key={i} className="flex flex-wrap items-center gap-3 text-xs bg-gray-800/60 border border-gray-700 rounded px-3 py-2">
                                          <span className="font-mono text-gray-400 shrink-0">{fmtDate(red.redeemed_at)}</span>
                                          <span className="text-white font-bold truncate max-w-[240px]">{red.redeemed_by_email || red.redeemed_by_user_id || '(nincs email)'}</span>
                                          {red.channel && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-bold uppercase">{red.channel === 'barion' ? 'Bankkártya' : 'Utalás'}</span>}
                                          <span className="text-emerald-400 font-bold ml-auto">–{fmtHuf(red.discount_huf || 0)} Ft</span>
                                          <span className="text-gray-500">→ {fmtHuf(red.amount_after_huf || 0)} Ft</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {sends.length > 0 && (
                                  <div>
                                    <div className="text-[11px] uppercase tracking-wide text-sky-400 font-bold mb-2">E-mail küldések ({sends.length})</div>
                                    <div className="space-y-1.5">
                                      {sends.map((s, i) => (
                                        <div key={i} className="flex flex-wrap items-center gap-3 text-xs bg-gray-800/60 border border-gray-700 rounded px-3 py-2">
                                          <span className="font-mono text-gray-400 shrink-0">{fmtDate(s.sent_at)}</span>
                                          <span className="text-white truncate max-w-[240px]">{s.to_name ? `${s.to_name} · ` : ''}{s.to_email}</span>
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${s.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-300'}`}>
                                            {s.status === 'sent' ? 'Kézbesítve' : s.status === 'mocked' ? 'Mock' : s.status}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {!reds.length && !sends.length && (
                                  <p className="text-xs text-gray-500 italic">Még nincs esemény ehhez a kuponhoz.</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* QR code dialog — the customer scans it with their phone, opens
          oroszcoaching.hu/?coupon={CODE} which auto-fills the checkout. */}
      <Dialog open={!!qrCode} onOpenChange={(o) => !o && setQrCode(null)}>
        <DialogContent className="max-w-sm bg-white text-gray-900" data-testid="admin-coupons-qr-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">QR kód a kuponhoz</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              A telefon kamerája beolvassa és megnyitja a főoldalt előre kitöltött kuponkóddal — csak rá kell koppintania.
            </DialogDescription>
          </DialogHeader>
          {qrCode && (
            <div className="text-center py-2">
              <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-xl">
                <QRCodeSVG value={couponUrl(qrCode.code)} size={220} level="M" includeMargin={false} />
              </div>
              <div className="mt-3 font-mono font-bold text-yellow-700 bg-yellow-100 inline-block px-3 py-1 rounded text-lg">{qrCode.code}</div>
              <div className="text-[11px] text-gray-500 mt-2 break-all">{couponUrl(qrCode.code)}</div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyCode(couponUrl(qrCode.code))}
                className="mt-3"
                data-testid="admin-coupons-copy-qr-url"
              >
                <Copy size={12} className="mr-1" /> URL másolása
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email send dialog */}
      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent className="max-w-md bg-white text-gray-900" data-testid="admin-coupons-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Kupon küldése emailben</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Egy márkázott e-mail megy ki a kóddal + „Fizetek most" gombbal, ami előre kitöltött kuponnal viszi a fizetéshez.
            </DialogDescription>
          </DialogHeader>
          {emailFor && (
            <div className="space-y-3">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-sm">
                <span className="font-bold">Kupon:</span> <code className="font-mono">{emailFor.code}</code> — {emailFor.discountLine}
              </div>
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Címzett e-mail cím *</Label>
                <Input
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                  placeholder="ugyfel@example.com"
                  data-testid="admin-coupons-email-to"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Címzett neve (opcionális)</Label>
                <Input
                  value={emailForm.to_name}
                  onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })}
                  placeholder="Kovács Béla"
                  data-testid="admin-coupons-email-name"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Rövid üzenet (opcionális)</Label>
                <Textarea
                  value={emailForm.intro_message}
                  onChange={(e) => setEmailForm({ ...emailForm, intro_message: e.target.value })}
                  placeholder="A konzultáción beszéltünk erről a kedvezményről. Ha bármi kérdés van, írj bátran!"
                  className="min-h-[80px]"
                  data-testid="admin-coupons-email-message"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setEmailFor(null)}>Mégse</Button>
                <Button
                  onClick={sendEmail}
                  disabled={emailBusy || !emailForm.to_email.trim()}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
                  data-testid="admin-coupons-email-send"
                >
                  {emailBusy ? <><Loader2 className="mr-2 animate-spin" size={14} /> Küldés…</> : <><Mail size={14} className="mr-2" /> Küldés</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCouponsPage;
